import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import { ArrowRight, ArrowLeft, Search } from "lucide-react";

/**
 * VAULT Dictionary — concise, educational explanations of the most useful
 * personal-finance-organisation terms. Every entry links to a related
 * article or product surface. No SEO padding; no advice.
 */
const ENTRIES = [
    { term: "Asset",              body: "Something a household owns that has value and could reasonably become cash — including savings, investments, property, and vehicles.", read: "bank-balance-vs-net-worth", feature: "/net-worth" },
    { term: "Liability",          body: "Something a household owes. Home loans, car loans, education loans, credit-card balances, and any personal borrowing all count.", read: "bank-balance-vs-net-worth", feature: "/net-worth" },
    { term: "Net Worth",          body: "Assets minus liabilities. The single number that describes financial position better than any balance ever can — and the one that changes slowly enough to trust.", read: "bank-balance-vs-net-worth", feature: "/net-worth" },
    { term: "Statement Balance",  body: "The total owed on a credit card for the most recent billing cycle. Different from the current balance, and different from the minimum due.", read: "information-problem", feature: "/payments" },
    { term: "Payment Due Date",   body: "The last date by which a payment must reach the biller before it is considered late. Missing this date is almost always an organisation problem — not a money problem.", read: "information-problem", feature: "/calendar" },
    { term: "Recurring Payment",  body: "A charge that repeats on a schedule — monthly, quarterly, annually. The word 'recurring' is a warning that it deserves periodic review.", read: "subscriptions", feature: "/subscriptions" },
    { term: "Subscription",       body: "A recurring charge for continued access to a service. Easy to start. Easier to forget — especially the annual ones.", read: "subscriptions", feature: "/subscriptions" },
    { term: "Renewal",            body: "The moment a subscription, insurance policy, warranty, or membership continues into another period. The organised household plans for it. The disorganised one is surprised by it.", read: "subscriptions", feature: "/calendar" },
    { term: "Nominee",            body: "A person named to receive an asset (for example an insurance payout or bank balance) on the account-holder's death. Nomination is administrative; it does not always override a Will.", read: "family-vault-checklist", feature: "/documents" },
    { term: "Beneficiary",        body: "A person legally entitled to receive an asset — often as a result of a Will, a trust, or a specific financial instrument.", read: "family-vault-checklist", feature: "/documents" },
    { term: "Warranty",           body: "A promise made by a seller or manufacturer to repair or replace a product for a set period. Kept together with the receipt; useless if either is missing.", read: "family-vault-checklist", feature: "/documents" },
    { term: "Insurance Policy",   body: "A contract in which a household pays a premium in exchange for financial protection against a defined risk. The renewal date matters as much as the coverage itself.", read: "family-vault-checklist", feature: "/documents" },
    { term: "Financial Document", body: "Any record that would be needed to prove, transfer, claim, or continue a financial arrangement. Value is determined the day it is needed — not the day it is filed.", read: "family-vault-checklist", feature: "/documents" },
    { term: "Joint Asset",        body: "An asset owned by two or more people together. Usually shared visibility, always shared responsibility.", read: "couples-money-privacy", feature: "/together" },
];

export default function Dictionary() {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return ENTRIES;
        return ENTRIES.filter(e =>
            e.term.toLowerCase().includes(term) || e.body.toLowerCase().includes(term));
    }, [q]);

    return (
        <PublicShell>
            <div className="vd-root" data-testid="dictionary">
                <style>{VD_CSS}</style>
                <section className="vd-container vd-hero">
                    <Link to="/learn" className="vd-back">
                        <ArrowLeft size={13}/> The VAULT Journal
                    </Link>
                    <div className="vd-eyebrow">VAULT DICTIONARY</div>
                    <h1 className="vd-title">Plain-English definitions for the terms that quietly run a financial life.</h1>
                    <p className="vd-lede">
                        Every definition links to an article that explains it in context — and, where relevant, to
                        the VAULT feature it belongs to.
                    </p>
                    <div className="vd-search">
                        <Search size={14} strokeWidth={2} className="vd-search__icon"/>
                        <input
                            type="text"
                            placeholder="Search terms…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="vd-search__input"
                            data-testid="dict-search"
                        />
                    </div>
                </section>

                <section className="vd-container vd-list" data-testid="dict-list">
                    {filtered.length === 0 && (
                        <p className="vd-empty">No matches. Try a broader term — or <Link to="/learn">browse the Journal</Link>.</p>
                    )}
                    {filtered.map((e) => (
                        <article key={e.term} className="vd-entry" data-testid={`dict-${e.term.toLowerCase().replace(/\s+/g,"-")}`}>
                            <h2 className="vd-entry__term">{e.term}</h2>
                            <p className="vd-entry__body">{e.body}</p>
                            <div className="vd-entry__row">
                                <Link to={`/learn/articles/${e.read}`} className="vd-entry__link">
                                    Read article <ArrowRight size={12}/>
                                </Link>
                                <Link to={e.feature} className="vd-entry__link vd-entry__link--muted">
                                    Related VAULT feature
                                </Link>
                            </div>
                        </article>
                    ))}
                </section>
            </div>
        </PublicShell>
    );
}

const VD_CSS = `
.vd-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.vd-container { max-width: 860px; margin: 0 auto; padding: 0 28px; }
.vd-hero { padding: 72px 28px 40px; }
.vd-back { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #57534E; text-decoration: none; margin-bottom: 20px; }
.vd-back:hover { color: #0F1E3A; }
.vd-eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.vd-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(30px, 4.4vw, 46px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; color: #0F1E3A; margin: 0 0 18px; }
.vd-lede { font-size: 15.5px; line-height: 1.6; color: #57534E; max-width: 620px; margin: 0 0 26px; }
.vd-search { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.12); border-radius: 14px; max-width: 420px; }
.vd-search__icon { color: #78716C; }
.vd-search__input { flex: 1; border: none; background: transparent; font-size: 14px; color: #0F1E3A; outline: none; }
.vd-search__input::placeholder { color: #a8a29e; }
.vd-list { padding: 20px 28px 0; display: flex; flex-direction: column; gap: 14px; }
.vd-entry { padding: 22px 26px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 18px; transition: transform 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms cubic-bezier(0.16,1,0.3,1); }
.vd-entry:hover { border-color: rgba(198,168,107,0.28); transform: translateY(-1px); }
.vd-entry__term { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; font-weight: 500; letter-spacing: -0.012em; color: #0F1E3A; margin: 0 0 8px; }
.vd-entry__body { font-size: 15px; line-height: 1.65; color: #292524; margin: 0 0 14px; }
.vd-entry__row { display: flex; gap: 16px; flex-wrap: wrap; }
.vd-entry__link { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; text-decoration: none; }
.vd-entry__link:hover { color: #C6A86B; }
.vd-entry__link--muted { color: #78716C; font-weight: 500; }
.vd-empty { font-size: 14.5px; color: #78716C; padding: 20px; }
.vd-empty a { color: #C6A86B; text-decoration: none; }
`;
